// getToObject.js
'use strict';

import { matchPath } from 'react-router';
import {
  globalAllowedSearchParams,
  allowedSearchParams,
} from '../resource/routeConstants.js';
import { removeLangFromPath } from '../resource/langPath.js';

/**
 * @typedef Path
 * @property {string} pathname - A URL pathname, beginning with a /.
 * @property {string} search - A URL search string, beginning with a ?.
 * @property {string} hash - A URL fragment identifier, beginning with a #.
 */

/**
 * Find the allowed search parameters for a given pathname
 * @param {string} pathname - The current pathname
 * @returns {string[]} The allowed search parameters
 */
const findAllowedSearchParams = pathname => {
  const normalizedPathname = removeLangFromPath(pathname);
  return Object.entries(allowedSearchParams).find(([pattern]) => {
    const matches = matchPath(pattern, normalizedPathname);
    return matches !== null;
  })?.[1];
};

/**
 * Filter URL parameters and keep only those in the whitelist
 * @param {string} search - URL search string
 * @param {string} pathname - Current pathname
 * @returns {string} Filtered search string
 */
const filterSearch = (pathname = '', search = '') => {
  const filteredParams = new URLSearchParams();
  const allowedParams = findAllowedSearchParams(pathname) || [];

  // Sort params to improve cache-ability
  [...new URLSearchParams(search)].sort().forEach(([key, value]) => {
    if (
      globalAllowedSearchParams.includes(key) ||
      allowedParams.includes(key)
    ) {
      filteredParams.append(key, value);
    }
  });

  return filteredParams.toString() ? `?${filteredParams.toString()}` : '';
};

/**
 * Parses a string URL path into its separate pathname, search, and hash components.
 * @param {string} {path} - url path.
 * @return {Path}
 */
export const parsePath = ({ path }) => {
  if (!path) return {};

  try {
    const url = new URL(path, 'https://swag.live');
    const hasPath = path.startsWith('/');

    return {
      ...(url.hash && { hash: url.hash }),
      ...(url.search && { search: url.search }),
      ...(hasPath && url.pathname && { pathname: url.pathname }),
    };
  } catch (_) {
    return {};
  }
};

/**
 * Get to object with filtered search params and language parameter
 * @param {string|Path} {[to = '']} - destination url.
 * @param {string} {[language = 'en']} - language code.
 * @param {boolean} {[shouldFilterSearch = false]} - filter search params.
 * @return {Path}
 */
const getToObject = ({ to = '', shouldFilterSearch = false } = {}) => {
  const path = typeof to === 'object' ? { ...to } : parsePath({ path: to });

  const search = shouldFilterSearch
    ? filterSearch(path.pathname, path.search)
    : path.search;

  return {
    ...path,
    search,
  };
};

export default getToObject;
